import { CommonMultiDeleteModel, FilteredPagedModel, FilteredPagedQueryResult } from "../../entities/common";
import { NotificationInfoModel, NotificationModel, SendNotificationModel } from "../../entities/notification";
import { getQuerySearchParams } from "../../utils";
import { identitySlice, TagType } from "./identityApi";

export const notificationsApi = identitySlice.injectEndpoints({
  endpoints: (builder) => ({
    getNotificationCommonInfo: builder.query<NotificationInfoModel, void>({
      query: () => "/notifications/commonInfo",
      providesTags: [{ type: TagType.Notification, id: "LIST" }],
    }),
    getNotificationsFilteredPaged: builder.query<FilteredPagedQueryResult<NotificationModel>, FilteredPagedModel>({
      query: ({ page = 1, pageSize = 10, searchTerm = "", sortFieldName = "", descending = false, ...rest }: FilteredPagedModel) => {
        let url = `/notifications/pageable?page=${page}&pageSize=${pageSize}&searchTerm=${searchTerm}&sortFieldName=${sortFieldName}&descending=${descending}`;
        const restParams = getQuerySearchParams(rest);
        if (restParams) {
          url += `&${restParams}`;
        }

        return url;
      },
      providesTags: (result, error, arg) =>
        result
          ? [
              { type: TagType.Notification, id: "PARTIAL-LIST" },
              ...result.data.map(({ id }) => ({
                type: TagType.Notification,
                id,
              })),
            ]
          : [{ type: TagType.Notification, id: "PARTIAL-LIST" }],
    }),
    getNotification: builder.query<NotificationModel, number>({
      query: (notificationId) => `/notifications/${notificationId}`,
      providesTags: (result, error, arg) => [{ type: TagType.Notification, id: arg }],
    }),
    sendNotification: builder.mutation({
      query: (model: SendNotificationModel) => ({
        url: "/notifications/send",
        method: "POST",
        body: model,
      }),
      invalidatesTags: [
        { type: TagType.Notification, id: "LIST" },
        { type: TagType.Notification, id: "PARTIAL-LIST" },
      ],
    }),

    activateNotification: builder.mutation({
      query: (notificationId: number) => ({
        url: `/notifications/${notificationId}/activate`,
        method: "PUT",
      }),
      invalidatesTags: (result, error, arg) => (error ? [] : [{ type: TagType.Notification, id: arg }]),
    }),
    deactivateNotification: builder.mutation({
      query: (notificationId: number) => ({
        url: `/notifications/${notificationId}/deactivate`,
        method: "PUT",
      }),
      invalidatesTags: (result, error, arg) => (error ? [] : [{ type: TagType.Notification, id: arg }]),
    }),
    deleteNotification: builder.mutation({
      query: (notificationId: number) => ({
        url: `/notifications/${notificationId}`,
        method: "DELETE",
      }),
      invalidatesTags: (result, error, arg) =>
        error
          ? []
          : [
              { type: TagType.Notification, id: arg },
              { type: TagType.Notification, id: "LIST" },
            ],
    }),
    deleteMultiNotifications: builder.mutation({
      query: (multiDeleteModel: CommonMultiDeleteModel) => ({
        url: "/notifications/multiDelete",
        method: "DELETE",
        body: multiDeleteModel,
      }),
      invalidatesTags: (result, error, arg) =>
        error
          ? []
          : [
              ...arg.ids.map((id) => ({
                type: TagType.Notification,
                id,
              })),
              { type: TagType.Notification, id: "LIST" },
            ],
    }),
  }),
});

export const {
  useGetNotificationCommonInfoQuery,
  useGetNotificationsFilteredPagedQuery,
  useGetNotificationQuery,
  useSendNotificationMutation,
  useActivateNotificationMutation,
  useDeactivateNotificationMutation,
  useDeleteNotificationMutation,
  useDeleteMultiNotificationsMutation,
} = notificationsApi;
